'use client';

import * as React from 'react';
import { Field } from '@chakra-ui/react';
import { FiCalendar, FiX } from 'react-icons/fi';

import { Box } from './box';
import { Input, InputGroup } from './input';
import { Stack } from './stack';
import { IconButton } from './icon-button';

export interface DatePickerProps {
  /**
   * Current date value (ISO string or Date object)
   */
  value?: string | Date | null;

  /**
   * Callback when date changes
   */
  onChange?: (date: string | null) => void;

  /**
   * Label for the field
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  hint?: string;

  /**
   * Minimum date (ISO string or Date)
   */
  min?: string | Date;

  /**
   * Maximum date (ISO string or Date)
   */
  max?: string | Date;

  /**
   * Whether the field is disabled
   */
  disabled?: boolean;

  /**
   * Whether the field is required
   */
  required?: boolean;

  /**
   * Whether to show clear button
   */
  clearable?: boolean;

  /**
   * Date format for display
   * @default 'YYYY-MM-DD'
   */
  dateFormat?: string;

  /**
   * Container className
   */
  containerClassName?: string;

  /**
   * Input size
   */
  inputSize?: 'sm' | 'md' | 'lg';

  /**
   * Additional input props
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      label,
      placeholder = 'Select date',
      error,
      hint,
      min,
      max,
      disabled,
      required,
      clearable = true,
      dateFormat: _dateFormat = 'YYYY-MM-DD',
      containerClassName,
      inputSize = 'md',
      inputProps = {},
    },
    ref
  ) => {
    // Convert value to string format for input
    const dateValue = React.useMemo(() => {
      if (!value) return '';

      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) return '';

      // Format as YYYY-MM-DD for HTML input
      return date.toISOString().split('T')[0];
    }, [value]);

    // Convert min/max to string format
    const minDate = React.useMemo(() => {
      if (!min) return undefined;
      const date = min instanceof Date ? min : new Date(min);
      return date.toISOString().split('T')[0];
    }, [min]);

    const maxDate = React.useMemo(() => {
      if (!max) return undefined;
      const date = max instanceof Date ? max : new Date(max);
      return date.toISOString().split('T')[0];
    }, [max]);

    // Handle date change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (onChange) {
        onChange(newValue || null);
      }
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
      e.preventDefault();
      if (onChange && !disabled) {
        onChange(null);
      }
    };

    const invalid = !!error;
    const inputId = React.useId();

    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box
            as="label"
            fontSize="sm"
            fontWeight="medium"
            marginBottom="8px"
            display="block"
            {...({ htmlFor: inputId } as Record<string, unknown>)}
          >
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Date Input */}
        <InputGroup>
          <Box position="relative" width="100%">
            <Input
              ref={ref}
              id={inputId}
              type="date"
              value={dateValue}
              onChange={handleChange}
              min={minDate}
              max={maxDate}
              disabled={disabled}
              placeholder={placeholder}
              paddingRight={clearable && dateValue ? 16 : 10}
              {...(({ size: _nativeSize, ...rest }) => rest)(inputProps)}
              inputSize={inputSize}
              style={{
                ...inputProps.style,
                colorScheme: 'light',
              }}
            />

            {/* Icons Container */}
            <Stack
              position="absolute"
              right={0}
              top="50%"
              transform="translateY(-50%)"
              direction="row"
              alignItems="center"
              paddingRight={2}
              gap={1}
            >
              {/* Clear button */}
              {clearable && dateValue && !disabled && (
                <IconButton
                  aria-label="Clear date"
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  tabIndex={-1}
                  icon={<FiX />}
                />
              )}

              {/* Calendar icon */}
              <Box color="#9CA3AF" pointerEvents="none">
                <FiCalendar />
              </Box>
            </Stack>
          </Box>
        </InputGroup>

        {/* Error Message */}
        {error && (
          <Box fontSize="sm" color="#EF4444" marginTop={1}>
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="sm" color="#6B7280" marginTop={1}>
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export { DatePicker };
